import { apiClient } from './apiClient';
import { apiGet, apiPost, apiDelete } from '../utils/api';

/**
 * MMM Configuration API Service
 * Handles communication between React frontend and Python backend
 */

import {
  MMMConfig,
  ConfigSaveResponse,
  ConfigLoadResponse,
  DataColumnsResponse,
  DataFrequency,
} from "../types/mmmConfig";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Type definitions for API responses with validation
 */
interface ValidateConfigResponse {
  success: boolean;
  message?: string;
  errors?: Array<{ field: string; message: string; type?: string }>;
}

interface SaveConfigResponseData {
  success: boolean;
  config_id?: string;
  name?: string;
  message?: string;
  errors?: any[];
}

interface LoadConfigResponseData {
  success: boolean;
  config?: MMMConfig;
  errors?: any[];
}



interface AnalysisStatusResponse {
  status: string;
  progress?: number;
  results?: { model_id?: string;[key: string]: any };
  error?: string;
}

/**
 * Helper function to validate response structure
 */
const validateResponse = (data: any, expectedKeys: string[]): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  return expectedKeys.every(key => key in data || data[key] !== undefined);
};

/**
 * Configuration API Service
 */
export const configService = {
  /**
   * Get available columns from uploaded data
   */
  async getDataColumns(logto: any, fileId: string): Promise<DataColumnsResponse> {
    try {
      return await apiGet<DataColumnsResponse>(
        `${API_BASE_URL}/api/configs/data-columns/${fileId}`,
        logto
      );
    } catch (error) {
      console.error("Error fetching data columns:", error);
      throw error;
    }
  },

  /**
   * Detect frequency from date column
   */
  async detectFrequency(logto: any, fileId: string, dateColumn: string): Promise<{ success: boolean; frequency: "D" | "W" | "M" | null }> {
    try {
      const url = `${API_BASE_URL}/api/configs/detect-frequency/${fileId}?date_column=${encodeURIComponent(dateColumn)}`;
      return await apiGet(url, logto);
    } catch (error) {
      console.error("Error detecting frequency:", error);
      return { success: false, frequency: null };
    }
  },

  /**
   * Validate configuration (without saving)
   */
  async validateConfig(logto: any, config: MMMConfig): Promise<ConfigSaveResponse> {
    try {
      const result = await apiPost<ValidateConfigResponse>(
        `${API_BASE_URL}/api/configs/validate`,
        logto,
        config
      );

      // Validate response structure
      if (!validateResponse(result, ['success'])) {
        console.error("[ERROR] Invalid validation response structure:", result);
        return {
          success: false,
          errors: [{ field: "general", message: "Invalid response format", type: "custom" }],
        };
      }

      return result as ConfigSaveResponse;
    } catch (error) {
      console.error("Error validating config:", error);
      throw error;
    }
  },

  /**
   * Save configuration
   */
  async saveConfig(
    logto: any,
    config: MMMConfig,
    dataFileId: string,
    configName: string
  ): Promise<ConfigSaveResponse> {
    try {
      console.log("[DEBUG] Saving config with data_file_id:", dataFileId);
      const result = await apiPost<SaveConfigResponseData>(
        `${API_BASE_URL}/api/configs/save`,
        logto,
        {
          config,
          data_file_id: dataFileId,
          config_name: configName,
        }
      );

      console.log("[DEBUG] Save successful:", result);

      // Validate response structure
      if (!validateResponse(result, ['success'])) {
        console.error("[ERROR] Invalid response structure:", result);
        return {
          success: false,
          message: "Invalid response from server",
          errors: [],
        };
      }

      return result as ConfigSaveResponse;
    } catch (error) {
      console.error("Error saving config:", error);
      throw error;
    }
  },

  /**
   * Load configuration by ID
   */
  async loadConfig(logto: any, configId: string): Promise<ConfigLoadResponse> {
    try {
      const result = await apiGet<LoadConfigResponseData>(
        `${API_BASE_URL}/api/configs/${configId}`,
        logto
      );

      // Validate response structure
      if (!validateResponse(result, ['success'])) {
        console.error("[ERROR] Invalid load config response structure:", result);
        throw new Error("Invalid response format from server");
      }

      return result as ConfigLoadResponse;
    } catch (error) {
      console.error("Error loading config:", error);
      throw error;
    }
  },

  /**
   * List all saved configurations
   */
  async listConfigs(logto: any): Promise<Array<{ id: string; name: string; created_at: string }>> {
    try {
      const data = await apiGet<{ success: boolean; configs: any[] }>(
        `${API_BASE_URL}/api/configs`,
        logto
      );
      return data.configs || [];
    } catch (error) {
      console.error("Error listing configs:", error);
      throw error;
    }
  },

  /**
   * Delete configuration by ID
   */
  async deleteConfig(logto: any, configId: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiDelete(
        `${API_BASE_URL}/api/configs/${configId}`,
        logto
      );
    } catch (error) {
      console.error("Error deleting config:", error);
      throw error;
    }
  },

  /**
   * Export configuration as YAML
   */
  async exportConfigYAML(config: MMMConfig): Promise<string> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/configs/export-yaml`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(config),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to export config: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success && data.yaml) {
        return data.yaml;
      }
      throw new Error(data.message || 'Failed to export YAML');
    } catch (error) {
      console.error("Error exporting config:", error);
      throw error;
    }
  },

  /**
   * Import configuration from YAML string
   */
  async importConfigYAML(yamlContent: string): Promise<ConfigLoadResponse> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/configs/import-yaml`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ yaml_content: yamlContent }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          config: {} as MMMConfig,
          errors: errorData.errors,
        };
      }

      return await response.json();
    } catch (error) {
      console.error("Error importing config:", error);
      throw error;
    }
  },

  /**
   * Run MMM analysis with configuration
   * Uses retry logic and clears model cache after successful training
   */
  async runAnalysis(
    logto: any,
    config: MMMConfig,
    dataFileId: string
  ): Promise<{ success: boolean; job_id: string; message?: string }> {
    try {
      // Build auth headers with fallback (same pattern as utils/api.ts)
      const authHeaders: Record<string, string> = {};

      // Try to get access token first
      try {
        const accessToken = await logto.getAccessToken();
        if (accessToken) {
          authHeaders['Authorization'] = `Bearer ${accessToken}`;
        }
      } catch (error) {
        // getAccessToken() throws in Logto v4+ when no API resources are configured
        console.debug('[configService] getAccessToken() failed, will try ID token claims fallback:', error);
      }

      // If no access token, try to get user ID from ID token claims as fallback
      if (!authHeaders['Authorization']) {
        try {
          const claims = await logto.getIdTokenClaims();
          if (claims?.sub) {
            // Pass user ID via X-User-Id header (backend supports this fallback)
            authHeaders['X-User-Id'] = claims.sub;
            console.debug('[configService] Using X-User-Id header fallback with user:', claims.sub);
          }
        } catch (error) {
          console.error('[configService] Failed to get ID token claims:', error);
        }
      }

      // If still no auth, check if user is authenticated
      if (!authHeaders['Authorization'] && !authHeaders['X-User-Id']) {
        const isAuthenticated = logto.isAuthenticated;
        if (!isAuthenticated) {
          return {
            success: false,
            job_id: "",
            message: "User is not authenticated. Please sign in.",
          };
        }
        console.warn('[configService] User is authenticated but no tokens available');
      }

      const result = await apiClient.post<{ success: boolean; job_id: string; message?: string }>(
        `${API_BASE_URL}/api/analysis/run`,
        {
          config,
          data_file_id: dataFileId,
        },
        {
          timeout: 60000, // 60 seconds for training requests
          retry: true,
          headers: authHeaders,
        }
      );

      // Clear model cache after starting training
      apiClient.clearCache('/api/v1/models');

      return result;
    } catch (error) {
      console.error("Error running analysis:", error);
      return {
        success: false,
        job_id: "",
        message: error instanceof Error ? error.message : "Failed to run analysis",
      };
    }
  },

  /**
   * Check analysis status
   */
  async checkAnalysisStatus(
    logto: any,
    jobId: string
  ): Promise<AnalysisStatusResponse> {
    try {
      const result = await apiGet<AnalysisStatusResponse>(
        `${API_BASE_URL}/api/analysis/status/${jobId}`,
        logto
      );

      // Validate response structure
      if (!validateResponse(result, ['status'])) {
        console.error("[ERROR] Invalid analysis status response structure:", result);
        throw new Error("Invalid response format from server");
      }

      return result;
    } catch (error) {
      console.error("Error checking analysis status:", error);
      throw error;
    }
  },
};

/**
 * Convert MMMConfig to YAML format (client-side)
 * Useful for preview before saving
 */
export const configToYAML = (config: MMMConfig): string => {
  const yaml: string[] = [];

  // Global settings
  yaml.push(`dep_var: ${config.dep_var}`);
  yaml.push(`date_var: ${config.date_var}`);
  yaml.push(`frequency: ${config.frequency || "W"}`);
  yaml.push(`regression_engine: ${config.regression_engine || "ridge"}`);
  if (config.data_path) yaml.push(`data_path: ${config.data_path}`);
  yaml.push("");

  // Media channels
  yaml.push("media_channels:");
  config.media_channels.forEach((channel) => {
    yaml.push(`  - name: ${channel.name}`);
    yaml.push(`    column: ${channel.column}`);
    yaml.push(`    adstock: ${channel.adstock || "geometric"}`);
    yaml.push(`    saturation: ${channel.saturation || "hill"}`);
  });
  yaml.push("");

  // Control variables
  if (config.control_vars && config.control_vars.length > 0) {
    yaml.push("control_vars:");
    config.control_vars.forEach((ctrl) => {
      yaml.push(`  - name: ${ctrl.name}`);
      yaml.push(`    column: ${ctrl.column}`);
    });
    yaml.push("");
  }

  // Feature Engineering
  if (config.feature_engineering) {
    yaml.push("feature_engineering:");
    yaml.push(`  enabled: ${config.feature_engineering.enabled}`);
    yaml.push(`  use_lagged_features: ${config.feature_engineering.use_lagged_features || false}`);
    yaml.push(`  use_interaction_terms: ${config.feature_engineering.use_interaction_terms || false}`);
    yaml.push(`  lag_periods: ${JSON.stringify(config.feature_engineering.lag_periods || [1, 2, 3])}`);
    if (config.feature_engineering.control_lag_periods) {
      yaml.push(`  control_lag_periods: ${JSON.stringify(config.feature_engineering.control_lag_periods)}`);
    }
    yaml.push("");
  }

  // Calendar
  if (config.calendar) {
    yaml.push("calendar:");
    yaml.push(`  use_trend: ${config.calendar.use_trend !== false}`);
    yaml.push(`  use_seasonality: ${config.calendar.use_seasonality !== false}`);
    yaml.push(`  seasonality_periods: ${JSON.stringify(config.calendar.seasonality_periods || [52])}`);
    yaml.push("");
  }

  // Decomposition
  if (config.decomposition) {
    yaml.push("decomposition:");
    yaml.push(`  use_prophet: ${config.decomposition.use_prophet !== false}`);
    if (config.decomposition.country_holidays) {
      yaml.push(`  country_holidays: ${config.decomposition.country_holidays}`);
    }
    yaml.push(`  yearly_seasonality: ${config.decomposition.yearly_seasonality !== false}`);
    yaml.push(`  weekly_seasonality: ${config.decomposition.weekly_seasonality !== false}`);
    yaml.push(`  daily_seasonality: ${config.decomposition.daily_seasonality || false}`);
    yaml.push("");
  }

  // Train/Test Split
  if (config.train_test_split) {
    yaml.push("train_test_split:");
    yaml.push(`  type: ${config.train_test_split.type || "time"}`);
    yaml.push(`  train_ratio: ${config.train_test_split.train_ratio}`);
    if (config.train_test_split.val_ratio !== null && config.train_test_split.val_ratio !== undefined) {
      yaml.push(`  val_ratio: ${config.train_test_split.val_ratio}`);
    }
    if (config.train_test_split.test_ratio !== null && config.train_test_split.test_ratio !== undefined) {
      yaml.push(`  test_ratio: ${config.train_test_split.test_ratio}`);
    }
    if (config.train_test_split.train_end_date) {
      yaml.push(`  train_end_date: ${config.train_test_split.train_end_date}`);
    }
    yaml.push("");
  }

  // Hyperparameters
  yaml.push("hyperparams:");
  yaml.push("  ridge_alpha:");
  yaml.push(`    lower: ${config.hyperparams.ridge_alpha.lower}`);
  yaml.push(`    upper: ${config.hyperparams.ridge_alpha.upper}`);
  yaml.push(`    logscale: ${config.hyperparams.ridge_alpha.logscale !== false}`);
  if (config.hyperparams.l1_ratio !== null && config.hyperparams.l1_ratio !== undefined) {
    yaml.push(`  l1_ratio: ${config.hyperparams.l1_ratio}`);
  }
  if (config.hyperparams.train_size) {
    yaml.push("  train_size:");
    yaml.push(`    lower: ${config.hyperparams.train_size.lower}`);
    yaml.push(`    upper: ${config.hyperparams.train_size.upper}`);
    yaml.push(`    logscale: ${config.hyperparams.train_size.logscale || false}`);
  }
  yaml.push("");

  // Optimization
  if (config.optimization) {
    yaml.push("optimization:");
    yaml.push(`  budget: ${config.optimization.budget || 2000}`);
    yaml.push(`  optimizer: ${config.optimization.optimizer || "OnePlusOne"}`);
    yaml.push("  objective_weights:");
    yaml.push(`    nrmse: ${config.optimization.objective_weights?.nrmse || 1.0}`);
    yaml.push(`    decomp_rssd: ${config.optimization.objective_weights?.decomp_rssd || 1.0}`);
    yaml.push(`    mape_lift: ${config.optimization.objective_weights?.mape_lift || 0.0}`);
    yaml.push(`  multi_objective: ${config.optimization.multi_objective || false}`);
    yaml.push("");
  }

  // Lift Tests
  if (config.lift_tests?.enabled && config.lift_tests.lift_tests && config.lift_tests.lift_tests.length > 0) {
    yaml.push("lift_tests:");
    yaml.push(`  enabled: true`);
    yaml.push("  lift_tests:");
    config.lift_tests.lift_tests.forEach((test) => {
      yaml.push(`    - channel: ${test.channel}`);
      yaml.push(`      observed_lift: ${test.observed_lift}`);
      yaml.push(`      confidence_interval: ${test.confidence_interval || 0.95}`);
      if (test.sample_size) {
        yaml.push(`      sample_size: ${test.sample_size}`);
      }
    });
  }

  return yaml.join("\n");
};

/**
 * Parse YAML string to MMMConfig (simplified)
 * Note: Full YAML parsing should be done server-side
 * This is a simplified client-side helper
 */
export const yamlToConfig = (yamlContent: string): Partial<MMMConfig> => {
  // This is a placeholder - actual YAML parsing should be done on server
  // using PyYAML to ensure compatibility with Python backend
  try {
    // Try to extract basic fields
    const depVarMatch = yamlContent.match(/dep_var:\s*(.+)/);
    const dateVarMatch = yamlContent.match(/date_var:\s*(.+)/);
    const frequencyMatch = yamlContent.match(/frequency:\s*(.+)/);

    return {
      dep_var: depVarMatch ? depVarMatch[1].trim() : "",
      date_var: dateVarMatch ? dateVarMatch[1].trim() : "",
      frequency: (frequencyMatch ? frequencyMatch[1].trim() : "W") as DataFrequency,
    };
  } catch (error) {
    console.error("Error parsing YAML:", error);
    return {};
  }
};

/**
 * Download config as YAML file
 */
export const downloadConfigAsYAML = (config: MMMConfig, filename: string = "mmm_config.yaml") => {
  const yamlContent = configToYAML(config);
  const element = document.createElement("a");
  element.setAttribute("href", "data:text/yaml;charset=utf-8," + encodeURIComponent(yamlContent));
  element.setAttribute("download", filename);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

/**
 * Local storage utilities for form draft saving
 */
export const configStorage = {
  saveDraft(key: string, config: MMMConfig, datafileId: string): void {
    try {
      const draft = {
        config,
        datafileId,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(`mmm_draft_${key}`, JSON.stringify(draft));
    } catch (error) {
      console.error("Error saving draft:", error);
    }
  },

  loadDraft(key: string): { config: MMMConfig; datafileId: string } | null {
    try {
      const stored = localStorage.getItem(`mmm_draft_${key}`);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch (error) {
      console.error("Error loading draft:", error);
      return null;
    }
  },

  deleteDraft(key: string): void {
    try {
      localStorage.removeItem(`mmm_draft_${key}`);
    } catch (error) {
      console.error("Error deleting draft:", error);
    }
  },

  listDrafts(): Array<{ key: string; savedAt: string }> {
    try {
      const drafts: Array<{ key: string; savedAt: string }> = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("mmm_draft_")) {
          const draft = JSON.parse(localStorage.getItem(key)!);
          drafts.push({
            key: key.replace("mmm_draft_", ""),
            savedAt: draft.savedAt,
          });
        }
      }
      return drafts;
    } catch (error) {
      console.error("Error listing drafts:", error);
      return [];
    }
  },
};
